import React, { useState } from 'react';
import { Box, Button, Heading, HStack, Input, Select, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import MainLayout from '../../shared/components/MainLayout';
import { AccessGate } from '../../shared/access/AccessControls';
import { authorizationDecisionsApi as api } from '../../store/services/AuthorizationDecisionsService';
import type { DecisionQuery } from '../../store/responseTypes/authorizationDecisions';

const Decisions = () => {
  const [query, setQuery] = useState<DecisionQuery>({ limit: 50 });
  const [draft, setDraft] = useState({ subjectUserId: '', action: '', requestId: '', fromDate: '', toDate: '', allowed: '' });
  const { currentData, isFetching, isError, refetch } = api.useGetAuthorizationDecisionsQuery(query);
  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery({ limit: 50, subjectUserId: draft.subjectUserId || undefined, action: draft.action || undefined,
      requestId: draft.requestId || undefined, allowed: (draft.allowed || undefined) as DecisionQuery['allowed'],
      fromDate: draft.fromDate ? new Date(draft.fromDate).toISOString() : undefined,
      toDate: draft.toDate ? new Date(draft.toDate).toISOString() : undefined });
  };
  return <Box as="section">
    <Text>This log records authorization decisions, not whether an operation committed. The default range is the last seven days.</Text>
    <form onSubmit={apply}><HStack flexWrap="wrap">
      {(['subjectUserId', 'action', 'requestId'] as const).map(field => <Input key={field} aria-label={field} placeholder={field}
        value={draft[field]} onChange={e => setDraft({ ...draft, [field]: e.target.value })} />)}
      <Input aria-label="From date" type="datetime-local" value={draft.fromDate} onChange={e => setDraft({ ...draft, fromDate: e.target.value })} />
      <Input aria-label="To date" type="datetime-local" value={draft.toDate} onChange={e => setDraft({ ...draft, toDate: e.target.value })} />
      <Select aria-label="Decision result" value={draft.allowed} onChange={e => setDraft({ ...draft, allowed: e.target.value })}>
        <option value="">All decisions</option><option value="true">Allowed</option><option value="false">Denied</option>
      </Select>
      <Button type="submit" isDisabled={!!draft.fromDate !== !!draft.toDate || isFetching}>Apply filters</Button>
    </HStack></form>
    <Button onClick={() => refetch()} isDisabled={isFetching}>Reload decisions</Button>
    {isFetching && <Text role="status">Loading decisions...</Text>}
    {isError && <Text role="alert">Could not load decisions. Check the filters and date range.</Text>}
    {!isError && <Table><Thead><Tr><Th>Time / action</Th><Th>Subject / entity</Th><Th>Decision</Th><Th>Trace</Th></Tr></Thead><Tbody>
      {currentData?.decisions.map(d => <Tr key={d.id}><Td>{d.decidedAt}<br />{d.action} ({d.actionCode})</Td>
        <Td>{d.subjectUserId || d.subjectProfileId || '-'}<br />{d.entityType}: {d.entityId || '-'}</Td>
        <Td>{d.allowed ? 'Allowed' : 'Denied'}: {d.reason}<br />{d.platformRole}</Td>
        <Td><details><summary>Decision details</summary><Text>Request: {d.requestId || '-'}</Text>
          <Text>IP: {d.ip || '-'}</Text><Text>Acting company: {d.actingCompanyId || '-'}</Text>
          <Text>Relation: {d.matchedRelation || '-'}; via: {d.matchedVia || '-'}</Text>
          <Text>Failed attribute: {d.failedAttribute || '-'}; edges: {d.edgeCount}; duration: {d.durationMs ?? '-'} ms</Text>
        </details></Td></Tr>)}
    </Tbody></Table>}
    <HStack my={3}><Button isDisabled={isFetching || !query.cursor} onClick={() => setQuery({ ...query, cursor: undefined })}>First decisions</Button>
      <Button isDisabled={isFetching || !currentData?.pagination.nextCursor} onClick={() => {
        if (!currentData) return;
        // Retain the server-resolved range across keyset pages.
        setQuery({ ...query, cursor: currentData.pagination.nextCursor || undefined,
          fromDate: currentData.pagination.fromDate, toDate: currentData.pagination.toDate });
      }}>Next decisions</Button></HStack>
  </Box>;
};
const Partitions = () => {
  const { currentData, isError, isFetching, refetch } = api.useGetAuthorizationPartitionsQuery();
  return <Box as="section" mt={6}><Heading size="md">Log partitions</Heading>
    <Button onClick={() => refetch()} isDisabled={isFetching}>Reload partitions</Button>
    {isError && <Text role="alert">Could not load log partitions.</Text>}
    <Text>Minimum retention: {currentData?.retentionDays ?? '-'} days. Row counts are estimates.</Text>
    {currentData?.coverage.map(c => <Text key={c.month}>{c.month}: {c.present ? 'covered' : 'missing'}</Text>)}
    <Table><Thead><Tr><Th>Name / bounds</Th><Th>Estimated rows</Th><Th>Bytes / last analyzed</Th></Tr></Thead><Tbody>
      {currentData?.partitions.map(p => <Tr key={p.name}><Td>{p.name}<br />{p.bounds}</Td>
        <Td>{p.estimatedRows ?? 'Unavailable'}</Td><Td>{p.sizeBytes}<br />{p.lastAnalyzedAt || 'Not analyzed'}</Td></Tr>)}
    </Tbody></Table>
  </Box>;
};
const AuthorizationDecisions = () => <MainLayout><Box p={4}><Heading size="lg" mb={4}>Authorization decisions</Heading>
  <AccessGate action="ADMIN_AUTHORIZATION_DECISION_READ"><Decisions /></AccessGate>
  <AccessGate action="ADMIN_AUTHORIZATION_DECISION_PARTITIONS_READ"><Partitions /></AccessGate>
</Box></MainLayout>;
export default AuthorizationDecisions;
